import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ExternalLink,
  Sparkles,
  BookOpen,
  ShieldCheck,
  BadgeCheck,
  Radar,
  Users,
  Building2,
  GraduationCap,
  Lightbulb,
  Heart,
} from "lucide-react";
import { INITIATIVES } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const PILLAR_ICONS_ACADEMY = [BookOpen, Sparkles, BadgeCheck, Radar];
const PILLAR_ICONS_AIILM = [GraduationCap, ShieldCheck, Building2, Heart];

const Initiatives = () => {
  useEffect(() => window.scrollTo({ top: 0 }), []);
  const a = INITIATIVES.academy;
  const k = INITIATIVES.aiilm;

  return (
    <main data-testid="page-initiatives">
      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-32 right-10 w-[700px] h-[500px] glow-amber pointer-events-none" />
        <div className="absolute top-40 -left-20 w-[500px] h-[400px] glow-teal pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-8 border border-stone-300 px-3 py-1.5 backdrop-blur-md">
              <Sparkles className="w-3.5 h-3.5 text-[#B8860B]" />
              <span className="text-xs uppercase tracking-[0.25em] text-stone-700">
                ITHR Initiatives · The frontier, closer to home
              </span>
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-8 max-w-5xl">
              We don't just consult<br />
              on AI. We <span className="italic text-[#B8860B]">build the ground</span><br />
              others stand on.
            </h1>
            <p className="text-stone-600 text-lg md:text-xl max-w-2xl leading-relaxed">
              From the enterprise workforce to the classroom, our initiatives make agentic AI something people can learn, prove and steer — safely, verifiably, and at scale.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Academy — flagship */}
      <section id="academy" className="py-24 md:py-32 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                  {a.tag}
                </div>
                <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-6">
                  {a.name}.
                </h2>
                <p className="font-display text-stone-700 text-2xl md:text-3xl font-light leading-snug italic">
                  {a.short}
                </p>
              </div>
              <div className="lg:col-span-4 space-y-2">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{a.established}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{a.origin}</div>
              </div>
            </div>

            <p className="text-stone-700 text-lg leading-relaxed max-w-4xl mb-14">
              {a.summary}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-16" data-testid="academy-stats">
              {a.stats.map((s) => (
                <div key={s.label} className="bg-white p-8">
                  <div className="font-display text-4xl md:text-5xl text-stone-900 font-light tracking-tighter">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mt-3">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {a.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS_ACADEMY[i] || BookOpen;
              return (
                <Reveal key={p.title} delay={i * 80} className="bento-card p-8 md:p-10" data-testid={`academy-pillar-${i}`}>
                  <div className="flex items-start gap-5">
                    <Icon className="w-8 h-8 text-[#B8860B] mt-1 flex-shrink-0" strokeWidth={1.25} />
                    <div>
                      <h3 className="font-display text-xl md:text-2xl text-stone-900 mb-3">{p.title}</h3>
                      <p className="text-stone-600 leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Certification ladder */}
          <Reveal>
            <div className="border border-stone-200 bg-white p-8 md:p-12 mb-14">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                The Certification Ladder
              </div>
              <h3 className="font-display text-stone-900 text-2xl md:text-3xl font-light tracking-tight mb-8">
                Eight tiers. From analyst to CAIO.
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3" data-testid="academy-ladder">
                {a.ladder.map((t, i) => (
                  <div key={t} className="flex items-baseline gap-3 py-2 border-b border-stone-200">
                    <span className="font-mono text-[#B8860B] text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-stone-800">{t.replace(/^Tier [IVX]+ — /, "")}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Curriculum informed by */}
          <Reveal>
            <div className="mb-14">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-6">
                Curriculum informed by public research & standards from
              </div>
              <div className="flex flex-wrap gap-2" data-testid="academy-inspired-by">
                {a.inspiredBy.map((n) => (
                  <span key={n} className="inline-flex items-center px-4 py-2 bg-white border border-stone-200 text-stone-700 text-sm font-mono uppercase tracking-[0.1em]">
                    {n}
                  </span>
                ))}
              </div>
              <p className="text-xs text-stone-500 mt-4 italic">
                Independently authored by ITHR · not affiliated with the organizations above.
              </p>
            </div>
          </Reveal>

          {/* CTAs */}
          <Reveal>
            <div className="flex flex-wrap gap-4 pt-4 border-t border-stone-200">
              <a
                data-testid="academy-cta-primary"
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
              >
                Visit the Academy
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                data-testid="academy-cta-catalog"
                href={`${a.url}/courses`}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
              >
                Browse the catalog
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AIILM — Sparkl */}
      <section id="aiilm" className="py-24 md:py-32 border-t border-stone-200 bg-[#F5F1EA] scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold">{k.tag}</span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
                  </span>
                </div>
                <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-6">
                  {k.name}.
                </h2>
                <p className="font-display text-stone-700 text-2xl md:text-3xl font-light leading-snug italic">
                  {k.short}
                </p>
                <p className="mt-3 text-stone-500 font-mono text-xs uppercase tracking-[0.2em]">
                  AI + 'ilm (علم) — knowledge, in the language of the GCC
                </p>
              </div>
              <div className="lg:col-span-4 space-y-2">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{k.established}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{k.origin}</div>
              </div>
            </div>

            <p className="text-stone-700 text-lg leading-relaxed max-w-4xl mb-14">
              {k.summary}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-16" data-testid="aiilm-stats">
              {k.stats.map((s) => (
                <div key={s.label} className="bg-white p-8">
                  <div className="font-display text-3xl md:text-4xl text-stone-900 font-light tracking-tighter">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mt-3">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-14">
            {k.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS_AIILM[i] || Lightbulb;
              return (
                <Reveal key={p.title} delay={i * 70} className="bg-white p-8" data-testid={`aiilm-pillar-${i}`}>
                  <Icon className="w-8 h-8 text-[#B8860B] mb-6" strokeWidth={1.25} />
                  <h3 className="font-display text-lg text-stone-900 mb-3">{p.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{p.body}</p>
                </Reveal>
              );
            })}
          </div>

          {/* Five Big Ideas */}
          <Reveal>
            <div className="border border-stone-200 bg-white p-8 md:p-12 mb-14">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                The Five Big Ideas of AI
              </div>
              <h3 className="font-display text-stone-900 text-2xl md:text-3xl font-light tracking-tight mb-8">
                What every child learns.
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-testid="aiilm-big-ideas">
                {k.bigIdeas.map((idea, i) => (
                  <div key={idea.title} className="border-t border-stone-200 pt-5">
                    <div className="text-3xl mb-3">{idea.emoji}</div>
                    <h4 className="font-display text-base text-stone-900 mb-2 leading-tight">{idea.title}</h4>
                    <p className="text-xs text-stone-600 leading-relaxed">{idea.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Three bands */}
          <Reveal>
            <div className="mb-14">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                One programme · three bands
              </div>
              <h3 className="font-display text-stone-900 text-2xl md:text-3xl font-light tracking-tight mb-8">
                Growing with the child, 10 to 16.
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200" data-testid="aiilm-bands">
                {k.bands.map((band, i) => (
                  <div key={band.name} className="bg-white p-8">
                    <div className="font-mono text-[#B8860B] text-xs mb-3 uppercase tracking-[0.2em]">
                      {band.name} · Ages {band.ages}
                    </div>
                    <h4 className="font-display text-xl text-stone-900 mb-5">{band.tagline}</h4>
                    <ul className="space-y-2">
                      {band.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm text-stone-700">
                          <span className="text-[#B8860B] mt-0.5">•</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* CTAs */}
          <Reveal>
            <div className="border border-stone-300 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8" data-testid="aiilm-cta">
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-2">
                  For schools, teachers and parents
                </div>
                <h3 className="font-display text-stone-900 text-2xl md:text-3xl font-light tracking-tight">
                  Try a lesson with Sparky, or bring Sparkl to your school.
                </h3>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <a
                  href={k.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="aiilm-cta-visit"
                  className="btn-primary"
                >
                  Visit aiilm.me
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={`${k.url}#demo`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="aiilm-cta-demo"
                  className="btn-secondary"
                >
                  Try a demo lesson
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bottom quote / CTA */}
      <section className="py-24 md:py-32 border-t border-stone-200 text-center">
        <Reveal className="max-w-3xl mx-auto px-6">
          <Users className="w-10 h-10 text-[#B8860B] mx-auto mb-8" strokeWidth={1.25} />
          <p className="font-display text-stone-900 text-3xl md:text-4xl font-light leading-snug tracking-tight mb-10">
            "Consulting is what we do this year.<br />
            <span className="italic text-[#B8860B]">Enabling the next generation</span> is what we do this decade."
          </p>
          <Link to="/contact" data-testid="initiatives-bottom-cta" className="btn-primary">
            Partner with us
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default Initiatives;
